import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// RTK Query client for the Incentive ROI Dashboard scoring engine (thread
// 19ed22cbcc823b10). It talks to the read-only PR2 endpoint
// (/incentive-scoring/portal/score-window) which, given a challenge WINDOW
// (start/end datetime) and an optional cohort, answers Levi's #1 question:
// did the challenge HELP or HURT? — measured per rep per hour vs a matched
// same-weekday/same-clock-hours baseline. The Challenge Report Card page (PR3)
// is the only caller today. Server-side the endpoint is checkUser-gated; this
// page is additionally gated in-code to leadership (President + VP of Sales).
export const incentiveScoringApi = createApi({
  reducerPath: "incentiveScoringApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Score one incentive window. `params` mirrors the BFF query contract:
    //   start, end       (required) ISO/local datetime strings
    //   team             (optional) rep_summer.team_text LIKE filter
    //   baselineDays     (optional) how many prior same-weekdays to average
    //   costDollars      (optional) enables an ROI ratio in the response
    //   excludeDates     (optional) comma-separated YYYY-MM-DD to skip
    getScoreWindow: builder.query({
      query: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.start) qs.append("start", params.start);
        if (params.end) qs.append("end", params.end);
        if (params.team) qs.append("team", params.team);
        if (params.baselineDays) qs.append("baselineDays", params.baselineDays);
        if (params.costDollars) qs.append("costDollars", params.costDollars);
        if (params.excludeDates) qs.append("excludeDates", params.excludeDates);
        // mode "spike" scores an open-ended challenge by the spike since it was
        // issued; issuedAt anchors it, spikeDays is the after-window length.
        if (params.mode) qs.append("mode", params.mode);
        if (params.issuedAt) qs.append("issuedAt", params.issuedAt);
        if (params.spikeDays) qs.append("spikeDays", params.spikeDays);
        return {
          url: `/incentive-scoring/portal/score-window?${qs.toString()}`,
          method: "GET",
        };
      },
    }),
    // List every registered incentive + its score (feature A, thread 428). No
    // params — the Challenge Report Card page renders this so leadership never has
    // to know or type a window. Server-side checkUser-gated; page gates to leadership.
    getIncentives: builder.query({
      query: () => ({
        url: `/incentive-scoring/portal/incentives`,
        method: "GET",
      }),
    }),
  }),
});

export const { useLazyGetScoreWindowQuery, useGetIncentivesQuery } =
  incentiveScoringApi;
